import {Component, inject, OnInit, viewChild, signal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatIconModule} from "@angular/material/icon"
import {MatMenuModule} from "@angular/material/menu"
import {MatSlideToggleModule} from "@angular/material/slide-toggle"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NativeInputTextAreaComponent} from "@common/components/inputs/native/native-input-text-area/native-input-text-area.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {ContentTypeModel, MutationUpdateTaskInput, TaskState} from "@generated"
import {BaseDetailsComponent} from "@platform/components/base/base-details/base-details.component"
import {
    TaskDetailsCreateCommentGQL,
    TaskDetailsDeleteCommentGQL,
    TaskDetailsFragment,
    TaskDetailsGQL,
    UpdateTaskDetailsGQL,
} from "@platform/components/tasks/task-details/task-details.generated"
import {TasksService} from "@platform/services/tasks/tasks.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-task",
    templateUrl: "./task-details.component.html",
    styleUrls: ["./task-details.component.scss"],
    imports: [
        MatSlideToggleModule,
        RoutedDialogComponent,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        InputContainerComponent,
        NativeInputTextAreaComponent,
        ButtonComponent,
        TimeAgoPipe,
    ],
})
export class TaskDetailsComponent extends BaseDetailsComponent<TaskDetailsFragment, Omit<MutationUpdateTaskInput, "id">> implements OnInit {
    override _contentTypeModel = ContentTypeModel.Task
    readonly updateTaskGql = inject(UpdateTaskDetailsGQL)
    override _fetchItem = fetchThrowingErrors(inject(TaskDetailsGQL))
    override _updateItem = mutateThrowingErrors(this.updateTaskGql)

    readonly $textArea = viewChild.required<NativeInputTextAreaComponent>("cmInput")

    override paramName = "taskId"

    readonly tasks = inject(TasksService)
    readonly $comments = signal<TaskDetailsFragment["comments"]>([])

    readonly $newCommentText = signal("")
    dialogSizes = DialogSize

    readonly createCommentGql = inject(TaskDetailsCreateCommentGQL)
    readonly deleteCommentGql = inject(TaskDetailsDeleteCommentGQL)

    override ngOnInit() {
        super.ngOnInit()
        this.item$.subscribe((item) => {
            this.$comments.set(item?.comments ?? [])
        })
    }

    /**
     * Split user comment text into renderable segments: a sequence of plain text and link
     * tokens. Bound via *ngFor in the template, this avoids feeding the regex-mangled string
     * into [innerHTML] (which lets arbitrary HTML through outside of the URL match).
     */
    splitCommentSegments(text: string): {kind: "text" | "link"; value: string}[] {
        const segments: {kind: "text" | "link"; value: string}[] = []
        const linkPattern = /((http|https|ftp|ftps):\/\/[a-zA-Z0-9\-.]+\.[a-zA-Z]{2,3}(\/\S*)?)/g
        let lastIndex = 0
        for (const match of text.matchAll(linkPattern)) {
            const matchIndex = match.index ?? 0
            if (matchIndex > lastIndex) {
                segments.push({kind: "text", value: text.slice(lastIndex, matchIndex)})
            }
            segments.push({kind: "link", value: match[0]})
            lastIndex = matchIndex + match[0].length
        }
        if (lastIndex < text.length) {
            segments.push({kind: "text", value: text.slice(lastIndex)})
        }
        return segments
    }

    async addComment() {
        const task = this.$item()
        if (!task) {
            throw new Error("There is no selected task")
        }
        if (this.auth.$actingAsCustomer()) {
            await mutateThrowingErrors(this.updateTaskGql)({
                input: {
                    id: task.id,
                    state: TaskState.InProgress,
                },
            })
        }

        await mutateThrowingErrors(this.createCommentGql)({
            input: {
                taskId: task.id,
                text: this.$newCommentText(),
            },
        })
        this.$newCommentText.set("")
        this.$textArea().updateValue("")
        this.$textArea().focus()
        this.refresh.item(this.$item())
    }

    deleteComment(commentId: string): void {
        const task = this.$item()
        if (!task) {
            throw new Error("There is no selected task")
        }

        const oldComments = this.$comments()
        this.$comments.set(this.$comments().filter((comment) => comment.id !== commentId))
        this.notifications.withUndo(
            "Comment deleted",
            async () => {
                await mutateThrowingErrors(this.deleteCommentGql)({
                    id: commentId,
                })
                this.refresh.item(this.$item())
            },
            () => {
                this.$comments.set(oldComments)
            },
        )
    }

    ngOnDestroy() {
        this.refresh.contentTypeModel(ContentTypeModel.Task)
    }
}
